"use client";

import type React from "react";
import { useEffect, useState } from "react";

import {
  getAllCompanyMeasuresAcrossScorecards,
  getAllCompanyObjectivesAcrossScorecards,
} from "../../shared/apis/service/companyObjectiveService";
import { useAssistant } from "../../shared/hooks/useAssistant";
import { ScrollArea } from "./company-dashboard/ui/scroll-area";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./company-dashboard/ui/card";
import { Button } from "./company-dashboard/ui/button";
import { Textarea } from "./company-dashboard/ui/textarea";
import { Separator } from "./company-dashboard/ui/separator";

import {
  Lightbulb,
  Loader2,
  Send,
  RefreshCw,
  TrendingUp,
  AlertTriangle,
  BarChart3,
  Target,
  MessageSquare,
  Sparkles,
} from "lucide-react";
import { cn } from "../shared/utils/utils";

// Utility function to clean markdown formatting
const cleanMarkdownText = (text: string): string => {
  return text
    .replace(/\*\*(.*?)\*\*/g, "$1") // Remove bold formatting
    .replace(/\*(.*?)\*/g, "$1") // Remove italic formatting
    .replace(/#{1,6}\s/g, "") // Remove header formatting
    .replace(/`(.*?)`/g, "$1") // Remove code formatting
    .trim();
};

interface AnalysisInsight {
  type: "performance" | "trend" | "recommendation" | "alert";
  title: string;
  description: string;
  severity: "high" | "medium" | "low";
  icon?: React.ReactNode;
  data?: any;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface AIInsightsPanelProps {
  companyId: string;
}

export const AIInsightsPanel: React.FC<AIInsightsPanelProps> = ({
  companyId,
}) => {
  // Use the existing useAssistant hook
  const {
    messages: hookMessages,
    loading: hookLoading,
    sendQuestion,
    insights,
    autoAnalysis,
    refreshAnalysis,
  } = useAssistant(companyId);

  // Local state for UI management
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [quickInsights, setQuickInsights] = useState<AnalysisInsight[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [realTimeData, setRealTimeData] = useState<any>(null);

  // Convert hook messages to our Message format
  useEffect(() => {
    const convertedMessages: Message[] = hookMessages.map((msg, index) => {
      const isUser = msg.startsWith("🧑‍💼:");
      return {
        id: `${index}-${Date.now()}`,
        role: isUser ? "user" : "assistant",
        content: isUser ? msg.replace("🧑‍💼: ", "") : msg.replace("🤖: ", ""),
        timestamp: new Date(),
      };
    });
    setMessages(convertedMessages);
  }, [hookMessages]);

  // Fetch real-time data for insights
  const fetchRealTimeData = async () => {
    try {
      const [objectives, measures] = await Promise.all([
        getAllCompanyObjectivesAcrossScorecards(companyId),
        getAllCompanyMeasuresAcrossScorecards(companyId),
      ]);

      const analysisData = {
        objectives: objectives.slice(0, 15).map((o: any) => ({
          title: o.title || o.description,
          weight: o.weight,
          currentScore: o.currentScore,
          scorecardId: o.scorecardId,
          perspective: o.perspective,
        })),
        measures: measures.slice(0, 20).map((m: any) => ({
          description: m.description,
          q2Rating: m.q2FinalRating || 0,
          q4Rating: m.q4FinalRating || 0,
          target: m.annualTarget,
          actual: m.annualActual,
          scorecardId: m.scorecardId,
          perspective: m.perspective,
        })),
        summary: {
          totalObjectives: objectives.length,
          totalMeasures: measures.length,
          avgQ2Rating:
            measures.length > 0
              ? measures.reduce(
                  (sum: number, m: any) => sum + (m.q2FinalRating || 0),
                  0
                ) / measures.length
              : 0,
          avgQ4Rating:
            measures.length > 0
              ? measures.reduce(
                  (sum: number, m: any) => sum + (m.q4FinalRating || 0),
                  0
                ) / measures.length
              : 0,
          redMeasures: measures.filter((m: any) => (m.q2FinalRating || 0) <= 2)
            .length,
          greenMeasures: measures.filter(
            (m: any) => (m.q2FinalRating || 0) >= 4
          ).length,
          amberMeasures: measures.filter(
            (m: any) => (m.q2FinalRating || 0) > 2 && (m.q2FinalRating || 0) < 4
          ).length,
        },
      };

      setRealTimeData(analysisData);
      return analysisData;
    } catch (error) {
      console.error("Error fetching real-time data:", error);
      throw error;
    }
  };

  // Generate quick insights from real data
  const generateQuickInsights = async () => {
    try {
      const data = await fetchRealTimeData();
      const newInsights: AnalysisInsight[] = [];

      // Overall Performance Insight
      const avgRating = data.summary.avgQ2Rating;
      newInsights.push({
        type: "performance",
        title: "Overall Performance",
        description: `Average organizational rating: ${avgRating.toFixed(
          1
        )}/5.0 across ${data.summary.totalMeasures} KPIs`,
        severity: avgRating >= 4 ? "low" : avgRating >= 3 ? "medium" : "high",
        icon: <BarChart3 className="w-4 h-4" />,
      });

      // Performance Distribution
      const redPercentage =
        data.summary.totalMeasures > 0
          ? (data.summary.redMeasures / data.summary.totalMeasures) * 100
          : 0;
      if (redPercentage > 30) {
        newInsights.push({
          type: "alert",
          title: "High Risk Measures",
          description: `${
            data.summary.redMeasures
          } measures (${redPercentage.toFixed(0)}%) need immediate attention`,
          severity: "high",
          icon: <AlertTriangle className="w-4 h-4" />,
        });
      } else if (redPercentage > 15) {
        newInsights.push({
          type: "alert",
          title: "Moderate Risk",
          description: `${
            data.summary.redMeasures
          } measures (${redPercentage.toFixed(0)}%) require monitoring`,
          severity: "medium",
          icon: <AlertTriangle className="w-4 h-4" />,
        });
      }

      // Trend Analysis (Q2 vs Q4)
      const trendImprovement =
        data.summary.avgQ4Rating - data.summary.avgQ2Rating;
      if (Math.abs(trendImprovement) > 0.2) {
        newInsights.push({
          type: "trend",
          title: trendImprovement > 0 ? "Positive Trend" : "Declining Trend",
          description: `Performance ${
            trendImprovement > 0 ? "improved" : "declined"
          } by ${Math.abs(trendImprovement).toFixed(1)} points from Q2 to Q4`,
          severity: trendImprovement > 0 ? "low" : "high",
          icon: <TrendingUp className="w-4 h-4" />,
        });
      }

      // Green Measures Recognition
      if (data.summary.greenMeasures > 0) {
        const greenPercentage =
          (data.summary.greenMeasures / data.summary.totalMeasures) * 100;
        newInsights.push({
          type: "recommendation",
          title: "Strong Performers",
          description: `${
            data.summary.greenMeasures
          } measures (${greenPercentage.toFixed(0)}%) are exceeding targets`,
          severity: "low",
          icon: <Target className="w-4 h-4" />,
        });
      }

      // Data Coverage
      if (
        data.summary.totalObjectives === 0 ||
        data.summary.totalMeasures === 0
      ) {
        newInsights.push({
          type: "alert",
          title: "Limited Data",
          description:
            "Insufficient performance data available for comprehensive analysis",
          severity: "medium",
          icon: <AlertTriangle className="w-4 h-4" />,
        });
      }

      setQuickInsights(newInsights);
    } catch (error) {
      console.error("Error generating quick insights:", error);
      setQuickInsights([
        {
          type: "alert",
          title: "Data Error",
          description: "Unable to load performance data for analysis",
          severity: "high",
          icon: <AlertTriangle className="w-4 h-4" />,
        },
      ]);
    }
  };

  // Handle manual refresh
  const handleRefreshAnalysis = async () => {
    setAnalysisLoading(true);
    setAnalysisError(null);

    try {
      await Promise.all([refreshAnalysis(), generateQuickInsights()]);
    } catch (error) {
      console.error("Error refreshing analysis:", error);
      setAnalysisError("Failed to refresh analysis. Please try again later.");
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Handle chat submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const question = input.trim();
    setInput("");
    setIsLoading(true);

    try {
      await sendQuestion(question);
    } catch (error) {
      console.error("Error sending question:", error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content:
          "I'm sorry, I encountered an error processing your request. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "high":
        return "bg-red-100 text-red-800 border-red-200";
      case "medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "low":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Initialize data on mount
  useEffect(() => {
    if (companyId) {
      generateQuickInsights();
    }
  }, [companyId]);

  // Update loading state based on hook
  useEffect(() => {
    setIsLoading(hookLoading);
  }, [hookLoading]);

  return (
    <Card className="shadow-lg h-full flex flex-col">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl font-bold text-gray-900 flex items-center">
          <Sparkles className="w-5 h-5 mr-2 text-purple-500" />
          AI Performance Insights
        </CardTitle>
        <CardDescription className="text-sm text-gray-600">
          Get AI-powered analysis and recommendations based on your real
          performance data
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col space-y-4 overflow-hidden">
        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            onClick={handleRefreshAnalysis}
            disabled={analysisLoading || hookLoading}
            className="flex-1"
            variant="default"
          >
            {analysisLoading || hookLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh Analysis
              </>
            )}
          </Button>
        </div>

        {/* Error Display */}
        {analysisError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{analysisError}</p>
          </div>
        )}

        {/* Quick Insights */}
        {quickInsights.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center">
              <Lightbulb className="w-4 h-4 mr-1" />
              Quick Insights
            </h3>
            <div className="grid grid-cols-1 gap-2">
              {quickInsights.map((insight, index) => (
                <div
                  key={index}
                  className={`p-2 rounded-lg border text-xs ${getSeverityColor(
                    insight.severity
                  )}`}
                >
                  <div className="flex items-center gap-2">
                    {insight.icon}
                    <span className="font-medium">{insight.title}</span>
                  </div>
                  <p className="mt-1 opacity-90">{insight.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Detailed Analysis from Hook */}
        {autoAnalysis && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">
              AI Analysis Report
            </h3>
            <ScrollArea className="h-48 w-full">
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-gray-800 whitespace-pre-wrap border border-blue-200">
                {cleanMarkdownText(autoAnalysis)}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Performance Insights from Hook */}
        {insights.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">
              Performance Alerts
            </h3>
            <div className="space-y-1">
              {insights.map((insight, index) => (
                <div
                  key={index}
                  className={`p-2 rounded-lg border text-xs ${getSeverityColor(
                    insight.severity
                  )}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{insight.title}</span>
                  </div>
                  <p className="mt-1 opacity-90">{insight.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <Separator className="my-2" />

        {/* Chat Interface */}
        <div className="flex-1 flex flex-col min-h-0">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center mb-2">
            <MessageSquare className="w-4 h-4 mr-1" />
            Ask AI Assistant
          </h3>

          <ScrollArea className="flex-1 mb-3">
            <div className="space-y-2 pr-2">
              {messages.length > 0 ? (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      "p-2 rounded-lg max-w-[85%] text-sm",
                      message.role === "user"
                        ? "bg-blue-500 text-white ml-auto"
                        : "bg-gray-100 text-gray-800"
                    )}
                  >
                    {message.role === "assistant"
                      ? cleanMarkdownText(message.content)
                      : message.content}
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <MessageSquare className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">
                    Ask me about performance trends, KPIs, or recommendations!
                  </p>
                  <div className="mt-2 text-xs text-gray-400">
                    <p>Try asking:</p>
                    <p>• "What are our top performing areas?"</p>
                    <p>• "Which KPIs need attention?"</p>
                    <p>• "How can we improve performance?"</p>
                  </div>
                </div>
              )}

              {isLoading && (
                <div className="bg-gray-100 text-gray-800 p-2 rounded-lg max-w-[85%] text-sm">
                  <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />
                  AI is analyzing your data...
                </div>
              )}
            </div>
          </ScrollArea>

          <form onSubmit={handleSubmit} className="flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about performance, trends, or recommendations..."
              className="flex-1 resize-none text-sm"
              rows={2}
              disabled={isLoading}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            />
            <Button
              type="submit"
              disabled={isLoading || !input.trim()}
              size="sm"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};
